import React from "react";
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import useReportComplianceTrend from "../hooks/useReportComplianceTrend";

/**
 * Component to display compliance trend indicator
 * @param {string} uid - Client user ID
 * @param {string} organizationId - Organization ID
 * @param {boolean} showDetails - Whether to show detailed trend information
 */
const ComplianceTrendIndicator = ({
  uid,
  organizationId,
  showDetails = false,
}) => {
  const trendAnalysis = useReportComplianceTrend(uid, organizationId);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "improving":
        return <TrendingUpIcon />;
      case "declining":
        return <TrendingDownIcon />;
      case "stable":
        return <TrendingFlatIcon />;
      case "insufficient_data":
        return <WarningIcon />;
      case "error":
        return <ErrorIcon />;
      case "loading":
        return <CircularProgress size={20} />;
      default:
        return <WarningIcon />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case "improving":
        return "success";
      case "declining":
        return "error";
      case "stable":
        return "info";
      case "insufficient_data":
        return "warning";
      case "error":
        return "error";
      case "loading":
        return "default";
      default:
        return "default";
    }
  };

  const getTrendLabel = (trend, changePercentage) => {
    switch (trend) {
      case "improving":
        return `+${Math.abs(changePercentage).toFixed(1)}%`;
      case "declining":
        return `${changePercentage.toFixed(1)}%`;
      case "stable":
        return `${changePercentage >= 0 ? "+" : ""}${changePercentage.toFixed(1)}%`;
      case "insufficient_data":
        return "Not enough data";
      case "error":
        return "Error";
      case "loading":
        return "Loading...";
      case "no_data":
        return "No data";
      default:
        return "Unknown";
    }
  };

  if (trendAnalysis.trend === "loading") {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading trend...
        </Typography>
      </Box>
    );
  }

  if (trendAnalysis.trend === "error") {
    return (
      <Chip
        icon={<ErrorIcon />}
        label="Error loading trend"
        color="error"
        variant="outlined"
        size="small"
      />
    );
  }

  return (
    <Box>
      <Tooltip
        title={`${trendAnalysis.message} (${trendAnalysis.confidence}% confidence, ${trendAnalysis.dataPoints} data points)`}
        arrow
      >
        <Chip
          icon={getTrendIcon(trendAnalysis.trend)}
          label={getTrendLabel(
            trendAnalysis.trend,
            trendAnalysis.changePercentage,
          )}
          color={getTrendColor(trendAnalysis.trend)}
          variant={trendAnalysis.confidence >= 70 ? "filled" : "outlined"}
          size="small"
        />
      </Tooltip>

      {showDetails && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary" display="block">
            {trendAnalysis.message}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Current: {trendAnalysis.currentScore}% | Previous:{" "}
            {trendAnalysis.previousScore}%
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Confidence: {trendAnalysis.confidence}% | Data Points:{" "}
            {trendAnalysis.dataPoints}
          </Typography>
          {trendAnalysis.statisticalValidity === "insufficient" && (
            <Typography variant="caption" color="warning.main" display="block">
              ⚠ Statistical validity insufficient for reliable trend analysis
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ComplianceTrendIndicator;
